import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, first, map, mergeMap, switchMap } from 'rxjs/operators';
import { ApiTypes } from '@app/api/api-types';
import { AiTranslateBackendService } from '@app/core/providers/backend/ai-translate-backend.service';
import { StatementsBackendService } from '@app/core/providers/backend/statements-backend.service';
import { NotificationService } from '@app/core/providers/notification/notification.service';
import { AccountService } from '@core/providers/account/account.service';
import { I18nBackendService } from '@core/providers/backend/i18n-backend.service';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { QuestionGuestPreviewComponent } from '@shared/components/foodback-questions/add-question-dialog/question-guest-preview/question-guest-preview.component';
import { FoodbackQuestionsService } from '@shared/components/foodback-questions/foodback-questions.service';
import { InfoFormBoxComponent } from '@shared/components/info-form-box/info-form-box.component';
import {
  FailedTranslationsByLanguage,
  failureOutlivesEdit,
  readAiTranslations,
  settleFailureOnEdit,
} from '@shared/components/translate-dialog/ai-translation-outcome';
import { TranslationLanguageListComponent } from '@shared/components/translation-language-list/translation-language-list.component';
import { CONSTANTS } from '@shared/constants';
import { UnsavedChangesService } from '@shared/guards';
import { I18nAvailableLanguage, I18nSupportedLanguage } from '@shared/interfaces/i18n.interface';
import {
  MissingTranslationsForLanguages,
  Statement,
  StatementGroup,
  StatementSavedStatus,
  StatementTranslationFromBackend,
  StatementType,
} from '@shared/interfaces/statements.interface';
import {
  AddedQuestion,
  AddQuestionDialogInput,
  AddQuestionDialogResult,
} from './add-question-dialog.interface';

export const addQuestionDialogConfig: MatDialogConfig = {
  width: '1104px',
  maxWidth: '96vw',
  maxHeight: '90vh',
  panelClass: 'add-question-dialog-panel',
  disableClose: true,
};

const QUESTION_MAX_LENGTH = 50;

/** Single field-id for the AI translate endpoint — the dialog only translates the question text. */
const AI_FIELD_KEY = 'statement';

const I18N = 'SHARED.FOODBACK_STATEMENTS.MAIN.ADD_QUESTION_MODAL';

/**
 * Questions created from the standard set carry placeholder texts
 * ("Custom question 1"…) that must stay editable even on answered questions.
 */
const CUSTOM_QUESTION_PLACEHOLDERS = [
  'Custom question 1',
  'Custom question 2',
  'Custom question 3',
  'Custom question 4',
];

/**
 * How much two questions in one category must overlap before they are called near-duplicates.
 * Tuned for precision over recall: the warning is advisory, so a false alarm on a valid question
 * costs more than letting a genuine near-duplicate through.
 */
const SIMILARITY_THRESHOLD = 0.6;

/** Shorter words are function words in the languages we ship ("the", "of", "our", "der", "de"). */
const MIN_CONTENT_WORD_LENGTH = 4;

interface CategoryChip {
  uuid: string;
  name: string;
  count: number;
}

/**
 * The significant words of a question — lowercased, stripped of accents and punctuation, and
 * without a trailing plural "s" so "toilets" and "toilet" compare equal. Questions built entirely
 * from short words fall back to all of them rather than comparing as empty.
 */
function contentWords(text: string): Set<string> {
  const words = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  const significant = words.filter(word => word.length >= MIN_CONTENT_WORD_LENGTH);

  return new Set(
    (significant.length > 0 ? significant : words).map(word =>
      word.length > MIN_CONTENT_WORD_LENGTH && word.endsWith('s') ? word.slice(0, -1) : word
    )
  );
}

/** Dice coefficient over two word sets: shared words relative to their combined size. */
function wordOverlap(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) {
    return 0;
  }

  let shared = 0;
  for (const word of a) {
    if (b.has(word)) {
      shared += 1;
    }
  }

  return (2 * shared) / (a.size + b.size);
}

@Component({
  selector: 'app-add-question-dialog',
  templateUrl: './add-question-dialog.component.html',
  styleUrls: ['./add-question-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
    MatButton,
    MatFormFieldModule,
    MatInput,
    MatIcon,
    MatSlideToggle,
    MatDivider,
    FormsModule,
    TranslationLanguageListComponent,
    QuestionGuestPreviewComponent,
    InfoFormBoxComponent,
    TranslatePipe,
  ],
})
export class AddQuestionDialogComponent implements OnInit {
  protected readonly data = inject<AddQuestionDialogInput>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<AddQuestionDialogComponent, AddQuestionDialogResult | undefined>>(
      MatDialogRef
    );
  private readonly destroyRef = inject(DestroyRef);
  private readonly statementsBackendService = inject(StatementsBackendService);
  private readonly aiTranslateService = inject(AiTranslateBackendService);
  private readonly foodbackQuestionsService = inject(FoodbackQuestionsService);
  private readonly notificationService = inject(NotificationService);
  private readonly i18nBackendService = inject(I18nBackendService);
  private readonly translateService = inject(TranslateService);
  private readonly accountService = inject(AccountService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  protected readonly I18N = I18N;

  /** Whether the current account has Foodback Intelligence enabled. */
  protected readonly isAiEnabled = toSignal(
    this.accountService.getAccountContext$().pipe(map(account => account.isAiEnabled)),
    { initialValue: false }
  );

  protected readonly isEdit = Boolean(this.data.statement);
  protected readonly maxLength = QUESTION_MAX_LENGTH;
  protected readonly defaultLanguage = signal<I18nSupportedLanguage>(this.data.defaultLanguage);
  /** Active languages excluding the default — the rows of the translation list. */
  protected readonly dialogLanguages = signal<I18nAvailableLanguage[]>(
    this.data.availableLanguages.filter(
      language => language.isActive && language.code !== this.data.defaultLanguage.code
    )
  );

  private readonly addedQuestions = signal<AddedQuestion[]>([]);

  /**
   * The account's categories with the questions created in this dialog folded in. The dialog
   * outlives its own saves, so without this a "Save & add another" run keeps reporting the
   * category as it was when the dialog opened.
   */
  protected readonly questionCategories = computed<StatementGroup[]>(() => {
    const added = this.addedQuestions();
    const categories = this.data.questionCategories ?? [];
    if (added.length === 0) {
      return categories;
    }

    return categories.map(category => {
      const forCategory = added.filter(question => question.categoryUuid === category.groupUuid);

      return forCategory.length === 0
        ? category
        : {
            ...category,
            statements: [
              ...category.statements,
              ...forCategory.map(question => this.toStatement(question, category)),
            ],
          };
    });
  });

  protected readonly categoryChips = computed<CategoryChip[]>(() =>
    this.buildCategoryChips(this.questionCategories())
  );
  protected readonly selectedCategoryUuid = signal<string | null>(null);

  protected readonly defaultValue = signal('');
  protected readonly translationValues = signal<Record<string, string>>({});
  protected readonly inactiveLanguages = signal<I18nSupportedLanguage[]>([]);
  protected readonly isSaving = signal(false);
  protected readonly isSuggesting = signal(false);
  /** Codes freshly filled by AI, badged until the user edits them. */
  protected readonly aiTranslatedCodes = signal<string[]>([]);
  /** Languages the last AI suggestion left unfilled, and why, until the user edits them. */
  protected readonly failedTranslations = signal<FailedTranslationsByLanguage>({});

  protected readonly isActive = signal(false);
  protected readonly isEnforced = signal(false);

  /** Scale-introduction stem per language code, used as floating labels + preview header. */
  protected readonly stemByCode = computed<Record<string, string>>(() => {
    const codes = [
      this.defaultLanguage().code,
      ...this.dialogLanguages().map(language => language.code),
      ...this.inactiveLanguages().map(language => language.code),
    ];

    return Object.fromEntries(codes.map(code => [code, this.buildStem(code)]));
  });
  protected readonly defaultStemLabel = computed(
    () => this.stemByCode()[this.defaultLanguage().code] ?? ''
  );
  protected readonly defaultPlaceholder = this.translateService.instant(
    `${I18N}.DEFAULT_PLACEHOLDER`
  ) as string;

  /** The in-progress question keyed by language code — feeds the preview. */
  protected readonly newQuestionByLang = computed<Record<string, string>>(() => ({
    ...this.translationValues(),
    [this.defaultLanguage().code]: this.defaultValue(),
  }));

  /** Languages excluding the default plus their translations — for the preview switcher. */
  protected readonly previewLanguages = computed<I18nAvailableLanguage[]>(() => [
    this.data.availableLanguages.find(language => language.code === this.defaultLanguage().code) ??
      this.defaultLanguage(),
    ...this.dialogLanguages(),
  ]);

  /** Languages locked because the question already has answers (may include the default). */
  protected readonly readonlyLangs: string[] = this.computeReadonlyLangs();
  protected readonly lockedInfoMessage: string | null =
    this.readonlyLangs.length > 0
      ? (this.translateService.instant('SHARED.QUESTIONS.TRANSLATIONS_LOCKED_INFO', {
          0: String(this.data.statement?.answersCount ?? 0),
        }) as string)
      : null;

  private readonly defaultInputRef = viewChild<ElementRef<HTMLInputElement>>('defaultInput');
  private initialValues: Record<string, string> = {};

  protected readonly selectedCategory = computed<CategoryChip | null>(
    () => this.categoryChips().find(chip => chip.uuid === this.selectedCategoryUuid()) ?? null
  );

  /** A "?" anywhere in the text — the scale introduction already asks the question. */
  protected readonly hasQuestionMark = computed(
    () =>
      this.defaultValue().includes('?') ||
      this.dialogLanguages().some(language =>
        (this.translationValues()[language.code] ?? '').includes('?')
      )
  );

  /** The existing question in the selected category this text would split its answers with. */
  protected readonly duplicateQuestion = computed<string | null>(() => {
    const text = this.defaultValue().trim();
    if (text.length < MIN_CONTENT_WORD_LENGTH) {
      return null;
    }

    const words = contentWords(text);
    let closest: string | null = null;
    let closestOverlap = 0;
    for (const question of this.existingQuestionTexts()) {
      const overlap = wordOverlap(words, contentWords(question));
      if (overlap > closestOverlap) {
        closestOverlap = overlap;
        closest = question;
      }
    }

    return closestOverlap >= SIMILARITY_THRESHOLD ? closest : null;
  });

  /**
   * Existing questions in the selected category, in the default language. `flexibleName` is
   * deliberately not consulted: on a group's first statement it carries the category's display
   * name, not a question text.
   */
  protected readonly existingQuestionTexts = computed<string[]>(() => {
    const uuid = this.selectedCategoryUuid();
    const defaultCode = this.defaultLanguage().code;
    const editedUuid = this.data.statement?.uuid;
    const group = this.questionCategories().find(category => category.groupUuid === uuid);

    return (group?.statements ?? [])
      .filter(statement => !editedUuid || statement.uuid !== editedUuid)
      .map(statement => statement.translations?.[defaultCode] ?? '')
      .filter(Boolean);
  });
  protected readonly showExisting = signal(false);

  protected readonly shareText = computed(() => {
    const existingCount = this.selectedCategory()?.count ?? 0;
    const total = existingCount + 1;

    return this.translateService.instant(`${I18N}.SHARE_TEXT`, {
      ordinal: total,
      category: this.selectedCategory()?.name ?? '',
      percentage: Math.round(100 / total),
    }) as string;
  });

  protected readonly filledTranslationsCount = computed(
    () =>
      this.dialogLanguages().filter(
        language =>
          (Boolean((this.translationValues()[language.code] ?? '').trim()) &&
            !this.failedTranslations()[language.code]) ||
          this.readonlyLangs.includes(language.code)
      ).length
  );
  protected readonly translationsStatus = computed(
    () =>
      this.translateService.instant(`${I18N}.TRANSLATIONS_STATUS`, {
        filled: this.filledTranslationsCount(),
        total: this.dialogLanguages().length,
      }) as string
  );

  /** Every active language filled — the gate for activating the question. */
  protected readonly canActivate = computed(
    () =>
      Boolean(this.defaultValue().trim()) &&
      this.filledTranslationsCount() === this.dialogLanguages().length
  );
  protected readonly effectiveActive = computed(() => this.isActive() && this.canActivate());
  protected readonly effectiveEnforced = computed(
    () => this.isEnforced() && this.effectiveActive()
  );

  protected readonly activationHint = computed(() => {
    if (this.canActivate()) {
      return this.translateService.instant(`${I18N}.ACTIVATION_READY`) as string;
    }
    if (!this.defaultValue().trim()) {
      return this.translateService.instant(`${I18N}.ACTIVATION_NEED_DEFAULT`) as string;
    }
    const remaining = this.dialogLanguages().length - this.filledTranslationsCount();

    return this.translateService.instant(`${I18N}.ACTIVATION_NEED_TRANSLATIONS`, {
      count: remaining,
    }) as string;
  });

  protected readonly readyText = computed(() => {
    if (!this.defaultValue().trim()) {
      return this.translateService.instant(`${I18N}.READY_NEED_QUESTION`) as string;
    }

    return this.canActivate()
      ? (this.translateService.instant(`${I18N}.READY_CAN_ACTIVATE`) as string)
      : (this.translateService.instant(`${I18N}.READY_SAVES_INACTIVE`) as string);
  });

  /** Result for the X-button close, so "Save & add another" additions still reach the caller. */
  protected readonly closeResult = computed<AddQuestionDialogResult | undefined>(() => {
    const addedQuestions = this.addedQuestions();

    return addedQuestions.length > 0
      ? { addedCount: addedQuestions.length, addedQuestions }
      : undefined;
  });

  /** Rejected AI text put in a field to shorten, still longer than the field allows. */
  protected readonly hasTextOverLimit = computed(() =>
    Object.entries(this.failedTranslations()).some(([code, failure]) =>
      failureOutlivesEdit(failure, this.translationValues()[code] ?? '')
    )
  );

  protected readonly canSave = computed(() => {
    if (
      this.isSaving() ||
      !this.selectedCategoryUuid() ||
      !this.defaultValue().trim() ||
      this.hasTextOverLimit()
    ) {
      return false;
    }

    return !this.isEdit || this.hasChanges();
  });

  private readonly hasChanges = computed(() => {
    const current: Record<string, string> = {
      ...this.translationValues(),
      [this.data.defaultLanguage.code]: this.defaultValue(),
    };
    const keys = new Set([...Object.keys(current), ...Object.keys(this.initialValues)]);

    return [...keys].some(key => (current[key] ?? '') !== (this.initialValues[key] ?? ''));
  });

  constructor() {
    // `disableClose` keeps Material from closing on ESC or a backdrop click; both still
    // notify us, so entered input can be confirmed away instead of silently discarded.
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.requestClose());
    this.dialogRef
      .keydownEvents()
      .pipe(
        filter(event => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.requestClose());
  }

  ngOnInit(): void {
    this.i18nBackendService
      .getAllSupportedLanguages$()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(allLanguages => {
        const cleanNameByCode = new Map(
          allLanguages.map(language => [language.code, language.name ?? language.code])
        );
        const withCleanName = <T extends I18nSupportedLanguage>(lang: T): T => ({
          ...lang,
          name: cleanNameByCode.get(lang.code) ?? lang.name,
        });

        this.defaultLanguage.update(withCleanName);
        this.dialogLanguages.update(languages => languages.map(withCleanName));
        this.inactiveLanguages.set(
          allLanguages.filter(
            language => !this.data.availableLanguages.find(active => active.code === language.code)
          )
        );
      });

    if (this.data.statement) {
      this.prefillFromStatement(this.data.statement);
    } else {
      this.selectedCategoryUuid.set(this.categoryChips()[0]?.uuid ?? null);
    }
  }

  protected selectCategory(uuid: string): void {
    if (this.isEdit) {
      return;
    }
    this.selectedCategoryUuid.set(uuid);
  }

  protected onDefaultInput(value: string): void {
    this.defaultValue.set(value);
  }

  protected onTranslationsChange(next: Record<string, string>): void {
    const previous = this.translationValues();
    const edited = Object.keys(next).filter(code => next[code] !== previous[code]);
    if (edited.length > 0) {
      this.aiTranslatedCodes.update(codes => codes.filter(code => !edited.includes(code)));
      this.failedTranslations.update(failures =>
        edited.reduce((settled, code) => settleFailureOnEdit(settled, code, next[code]), failures)
      );
    }
    this.translationValues.set(next);
  }

  protected toggleShowExisting(): void {
    this.showExisting.update(value => !value);
  }

  protected onActiveChange(checked: boolean): void {
    if (!this.canActivate()) {
      return;
    }
    this.isActive.set(checked);
    if (!checked) {
      this.isEnforced.set(false);
    }
  }

  protected onEnforceChange(checked: boolean): void {
    if (this.effectiveActive()) {
      this.isEnforced.set(checked);
    }
  }

  protected cancel(): void {
    this.close();
  }

  /** Closing via X, ESC or the backdrop must not silently drop what the user typed. */
  protected requestClose(): void {
    if (!this.hasChanges()) {
      this.close();
      return;
    }

    this.unsavedChangesService
      .confirmDiscard$()
      .pipe(first(), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.close());
  }

  protected suggestAll(): void {
    const source = this.defaultValue().trim();
    if (!source || this.isSuggesting() || !this.isAiEnabled()) {
      return;
    }

    const locked = new Set(this.readonlyLangs);
    const targetLanguages = [...this.dialogLanguages(), ...this.inactiveLanguages()]
      .map(language => language.code)
      .filter(code => !locked.has(code));
    if (targetLanguages.length === 0) {
      return;
    }

    this.isSuggesting.set(true);
    this.aiTranslateService
      .translate$('accounts', this.data.accountUuid, {
        sourceLanguage: this.defaultLanguage().code as ApiTypes.LanguageEnum,
        targetLanguages: targetLanguages as ApiTypes.LanguageEnum[],
        texts: { [AI_FIELD_KEY]: source },
        options: { maxLength: QUESTION_MAX_LENGTH, preservePlaceholders: false },
      })
      .pipe(
        finalize(() => this.isSuggesting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const result = response.results[AI_FIELD_KEY];
          const outcome = readAiTranslations([response]);
          const translations = outcome.translations[AI_FIELD_KEY] ?? {};
          // A failed language keeps the text it had, unmarked and not counted, with its reason shown.
          this.translationValues.update(current => ({ ...current, ...translations }));
          this.aiTranslatedCodes.set(Object.keys(translations));
          this.failedTranslations.set(outcome.failures[AI_FIELD_KEY] ?? {});
          if (outcome.failedFieldCount > 0 || result?.status === 'error') {
            this.notificationService.warnWithKey('SHARED.TRANSLATE_DIALOG.AI_PARTIAL_ERROR');
          }
        },
        error: (error: unknown) => {
          if ((error as { status?: number })?.status === 429) {
            this.notificationService.warnWithKey('SHARED.TRANSLATE_DIALOG.AI_RATE_LIMITED');
          } else {
            this.notificationService.errorSnackbarWithKey('SHARED.TRANSLATE_DIALOG.AI_ERROR');
          }
        },
      });
  }

  protected save(addAnother: boolean): void {
    if (!this.canSave()) {
      return;
    }

    if (this.data.statement) {
      this.saveEdit(this.data.statement);
    } else {
      this.saveNew(addAnother);
    }
  }

  private saveNew(addAnother: boolean): void {
    const categoryUuid = this.selectedCategoryUuid();

    if (!categoryUuid) {
      return;
    }

    const translations = this.buildTranslationsPayload();
    const wantActive = this.effectiveActive();
    const wantEnforced = this.effectiveEnforced();

    this.isSaving.set(true);
    this.statementsBackendService
      .addNewStatement$(this.data.accountUuid, { categoryUuid, translations })
      .pipe(
        switchMap((response: StatementSavedStatus) => {
          if (!response?.addStatement) {
            return of({ response, active: false });
          }

          return this.applyActivation$(response, wantActive, wantEnforced);
        }),
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ response, active }) => {
          if (!response?.addStatement) {
            this.notifyNotAdded(response);
            return;
          }

          this.addedQuestions.update(added => [
            ...added,
            {
              uuid: response.statementUuid,
              categoryUuid,
              translations: Object.fromEntries(
                translations.map(entry => [entry.lang, entry.value])
              ),
              statementActive: active,
            },
          ]);
          this.notifyAddResult(response, active);

          if (addAnother) {
            this.resetForNextQuestion();
          } else {
            this.close();
          }
        },
        error: () =>
          this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.GENERAL_ERROR'),
      });
  }

  /**
   * Honour the in-dialog Active/Enforce toggles by patching the fresh statement's
   * assignment. Skipped when the backend already landed in the desired state. A patch
   * failure still keeps the created question — the toggle is a follow-up, not the save.
   */
  private applyActivation$(
    response: StatementSavedStatus,
    wantActive: boolean,
    wantEnforced: boolean
  ) {
    const uuid = response.statementUuid;
    if (!uuid || (wantActive === response.statementActive && !wantEnforced)) {
      return of({ response, active: response.statementActive });
    }

    return this.statementsBackendService
      .setAccountStatementAssignment$(this.data.accountUuid, uuid, {
        isEnabled: wantActive,
        isEnforced: wantEnforced,
      })
      .pipe(
        // The stored state, not the requested one: activation is refused until every active
        // language is translated, so the two part company on a half-translated question.
        map(assignment => ({ response, active: assignment.isEnabled })),
        // The question is already created; a failed activation must not discard it.
        // Fall back to the backend's state so it is still reported (and flagged inactive).
        catchError(() => of({ response, active: response.statementActive }))
      );
  }

  /**
   * "You are about to publish question text" only holds for a question consumers are already being
   * asked and have answered: the edit changes what they see and re-labels answers already collected.
   * Editing a draft publishes nothing, so it saves straight away.
   */
  private confirmEdit$(): Observable<boolean> {
    const statement = this.data.statement;
    const isLiveWithAnswers = Boolean(statement?.isEnabled) && (statement?.answersCount ?? 0) > 0;

    return isLiveWithAnswers
      ? this.foodbackQuestionsService.displayEditConfirmationPopup$()
      : of(true);
  }

  private saveEdit(statement: Statement): void {
    this.confirmEdit$()
      .pipe(
        mergeMap(confirmed => {
          if (!confirmed) {
            return of(null);
          }

          this.isSaving.set(true);

          return this.statementsBackendService
            .editStatement$(statement.uuid, {
              accountUuid: this.data.accountUuid,
              categoryUuid: this.selectedCategoryUuid() ?? undefined,
              translations: this.buildTranslationsPayload(),
            })
            .pipe(finalize(() => this.isSaving.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          if (response) {
            this.notificationService.confirmWithKey('SHARED.NOTIFICATIONS.EDIT_RESULT_SUCCESS');

            if (response.missingTranslationsForLanguages) {
              this.foodbackQuestionsService.showMissingTranslationsNotification(
                response.missingTranslationsForLanguages
              );
            }
            this.dialogRef.close({
              addedCount: this.addedQuestions().length,
              editResponse: response,
            });
          }
        },
        error: () =>
          this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.GENERAL_ERROR'),
      });
  }

  private close(): void {
    this.dialogRef.close(this.closeResult());
  }

  private resetForNextQuestion(): void {
    this.defaultValue.set('');
    this.translationValues.set({});
    this.aiTranslatedCodes.set([]);
    this.failedTranslations.set({});
    this.isActive.set(false);
    this.isEnforced.set(false);
    this.showExisting.set(false);
    this.defaultInputRef()?.nativeElement.focus();
  }

  private notifyNotAdded(response: StatementSavedStatus | null | undefined): void {
    const languages = this.missingLanguageNames(response?.missingTranslationsForLanguages);

    if (!languages) {
      this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.GENERAL_ERROR');
      return;
    }

    this.notificationService.errorSnackbarWithKey('SHARED.QUESTIONS.ADD_MISSING_TRANSLATIONS', {
      languages,
    });
  }

  /** A question saved inactive gets one line: the languages holding it back, or just that it is off. */
  private notifyAddResult(response: StatementSavedStatus, active: boolean): void {
    this.notificationService.confirmWithKey('ACCOUNT.FOODBACK.NOTIFICATIONS.ADD_RESULT_SUCCESS');

    if (active) {
      return;
    }

    const languages = this.missingLanguageNames(response.missingTranslationsForLanguages);
    this.notificationService.warn(
      languages
        ? (this.translateService.instant('SHARED.QUESTIONS.ADD_SAVED_INACTIVE', {
            languages,
          }) as string)
        : (this.translateService.instant(
            'ACCOUNT.FOODBACK.NOTIFICATIONS.STATEMENT_NOT_ACTIVATED.TEXT'
          ) as string)
    );
  }

  /** The missing languages as a readable list, or null when nothing is missing. */
  private missingLanguageNames(
    missing: MissingTranslationsForLanguages | undefined
  ): string | null {
    const codes = FoodbackQuestionsService.missingTranslationCodes(missing ?? null);

    return codes.length > 0
      ? codes
          .map(
            code => this.dialogLanguages().find(language => language.code === code)?.name ?? code
          )
          .join(', ')
      : null;
  }

  /**
   * Locked languages are only sent back unchanged by the form anyway, and the
   * backend keeps translations that are omitted — so excluding them mirrors the
   * previous behavior of disabled form controls being dropped from the payload.
   */
  private buildTranslationsPayload(): StatementTranslationFromBackend[] {
    const all: Record<string, string> = {
      ...this.translationValues(),
      [this.data.defaultLanguage.code]: this.defaultValue(),
    };
    const locked = new Set(this.readonlyLangs);

    return Object.entries(all)
      .filter(([lang, value]) => !locked.has(lang) && Boolean(value?.trim()))
      .map(([lang, value]) => ({ lang, value }));
  }

  private prefillFromStatement(statement: Statement): void {
    const defaultCode = this.data.defaultLanguage.code;
    const translations = statement.translations ?? {};

    this.defaultValue.set(translations[defaultCode] ?? '');
    this.translationValues.set(
      Object.fromEntries(Object.entries(translations).filter(([lang]) => lang !== defaultCode))
    );
    this.initialValues = { ...translations };
    this.selectedCategoryUuid.set(statement.groupUuid ?? null);
  }

  private computeReadonlyLangs(): string[] {
    const statement = this.data.statement;

    if (!statement || (statement.answersCount ?? 0) === 0) {
      return [];
    }

    return Object.entries(statement.translations ?? {})
      .filter(([, value]) => Boolean(value) && !CUSTOM_QUESTION_PLACEHOLDERS.includes(value))
      .map(([lang]) => lang);
  }

  /** A question created in this dialog, shaped so it reads like any other in its category. */
  private toStatement(question: AddedQuestion, category: StatementGroup): Statement {
    return {
      uuid: question.uuid ?? '',
      groupUuid: category.groupUuid,
      groupName: category.groupName,
      translations: question.translations,
      isEnabled: question.statementActive,
      type: StatementType.CUSTOM,
      isOnlyStatementWithFullTranslations: false,
      answersCount: 0,
    };
  }

  private buildCategoryChips(questionCategories: StatementGroup[]): CategoryChip[] {
    return (questionCategories ?? [])
      .filter(
        category =>
          category.groupName !== CONSTANTS.CATEGORIES.NAMES.OVERALL_EXPERIENCE &&
          category.groupName !== CONSTANTS.CATEGORIES.NAMES.TAKEAWAY_OVERALL_EXPERIENCE
      )
      .map(category => ({
        uuid: category.groupUuid,
        name:
          category.statements[0]?.flexibleName ??
          (this.translateService.instant(
            `SHARED.BACKEND_LABELS.STATEMENT_CATEGORY.${this.fetchGroupNameFromKey(category.groupName).toUpperCase()}`
          ) as string),
        count: category.statements.length,
      }));
  }

  private fetchGroupNameFromKey(groupName: string): string {
    const name = groupName.split('.').pop() ?? groupName;

    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  private buildStem(code: string): string {
    const key = `SHARED.FOODBACK_STATEMENTS.EXAMPLES_SHORT.${code.toUpperCase()}`;
    const raw = this.translateService.instant(key) as string;
    const text = raw && raw !== key ? raw : this.defaultStemFallback();

    return `${text.replace(/[…\s.]+$/u, '')} …`;
  }

  private defaultStemFallback(): string {
    return this.translateService.instant(
      `SHARED.FOODBACK_STATEMENTS.EXAMPLES_SHORT.${this.data.defaultLanguage.code.toUpperCase()}`
    ) as string;
  }
}
